import { Navigate, useLocation } from 'react-router';
import { useSetupStatus } from '@/lib/setup-status';

/**
 * Sends an account that has started nothing to /welcome, from wherever it
 * arrives.
 *
 * ## Why this is in the shell and not on the auth screens
 *
 * It used to be a hook on login and signup, and that was wrong twice over.
 * First it hooked only login, on the reasoning that email verification always
 * stands between signup and a session — which `REQUIRE_EMAIL_VERIFICATION=false`
 * makes untrue. Then it hooked signup as well, and a Google sign-up still
 * walked past both: better-auth answers the OAuth callback with a 302 to the
 * `callbackURL`, so the browser lands on `/` without either screen ever
 * mounting, and a brand new account went straight to the dashboard.
 *
 * Patching the third path would have left a fourth. The question is not "what
 * happened on the way in" but "does this session belong in the app yet", so it
 * is asked where every session passes: inside `ProtectedRoute`. OAuth, the
 * auto sign-in that follows verification, a bookmarked deep link and a
 * returning tab all go through here.
 *
 * ## Only `neverStarted`
 *
 * Never `!complete`. That would drag an established planner to the setup page
 * every session over a signature they cleared on purpose. `neverStarted` can
 * be true at most until the planner does any one thing, so this is a bounce
 * that ends. Someone who lands there, does nothing and comes back will see it
 * again, which is right — nothing changed.
 *
 * ## Holding the page back
 *
 * The children are withheld while the lookup is in flight rather than rendered
 * and then yanked. A planner who is about to be moved to /welcome should not
 * watch a dashboard paint first. The cost is one request on a cold load, held
 * for five minutes by the shared query and already owed by `SetupCard` on the
 * dashboard.
 *
 * A failed lookup opens the gate. Someone who has just authenticated must not
 * be stranded on a spinner by it, and `SetupCard` catches an unfinished
 * account on the dashboard regardless.
 */
export default function FirstRunGate({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { data: status, isPending } = useSetupStatus();

  // The destination cannot be gated, or it is a loop. Its own Gmail round trip
  // returns to /welcome too, so that lands here as well.
  if (pathname === '/welcome') return <>{children}</>;

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="display-tight text-lg text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (status?.neverStarted) return <Navigate to="/welcome" replace />;

  return <>{children}</>;
}
